/**
 * Риски: how protected the account is right now.
 *
 * Three readings lead — portfolio risk, whether new entries are allowed, and
 * how far the record has been reconciled with the exchange — then what the
 * account has committed, anything the system flagged, and its positions.
 * An unknown is shown as «—» with its reason, never as a green default:
 * a calm screen built on missing data is the most dangerous screen there is.
 */
import { LeadStrip } from "@/design-system/LeadStrip";
import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import { NotObserved } from "@/design-system/states/States";
import { StatusBadge } from "@/design-system/StatusBadge";
import { CELL, TABLE_BLEED, TABLE_HEAD, TABLE_ROW } from "@/design-system/table";
import { grouped, money } from "@/shared/money/money";
import { useOverview } from "@/shared/overview/useOverview";
import { findingText, reasonText } from "@/shared/text/reasons";

import { useRisk } from "./useRisk";

const PORTFOLIO: Readonly<Record<string, string>> = {
  SAFE: "В лимитах",
  ELEVATED: "Повышен",
  CRITICAL: "Критический",
};

const PORTFOLIO_TONE: Readonly<Record<string, string>> = {
  SAFE: "text-risk-safe",
  ELEVATED: "text-risk-elevated",
  CRITICAL: "text-risk-critical",
};

/** What the cycle is doing, in words; an unknown phase as written. */
const PHASE: Readonly<Record<string, string>> = {
  IDLE: "Свободен",
  BUILDING_GRID: "Ставит сетку",
  WAITING_FIRST_FILL: "Ждёт входа",
  ACTIVE_POSITION: "В позиции",
  TRAILING: "Трейлинг",
  CLOSING: "Закрывается",
  RECONCILING: "Сверяется",
  COMPLETE: "Завершён",
  RESELECT: "Выбирает монету",
  PAUSED: "На паузе",
  EMERGENCY_EXIT: "Аварийный выход",
  QUARANTINED: "Карантин",
  LIVE: "Работает",
  EMERGENCY_STOPPED: "Аварийно остановлен",
};

function PhaseBadge({ phase }: { readonly phase: string }): React.JSX.Element {
  const tone = phase.startsWith("EMERGENCY") ? "danger" : phase === "PAUSED" || phase === "QUARANTINED" ? "warning" : "active";
  return <StatusBadge tone={tone}>{PHASE[phase] ?? phase}</StatusBadge>;
}

function moment(iso: string | null): string {
  if (iso === null) return "—";
  return new Date(iso).toLocaleString("ru-RU", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" });
}

function amount(value: string | null | undefined): React.JSX.Element {
  return value == null ? <span>—</span> : <span title={value}>{money(value)}</span>;
}

export function RiskPage(): React.JSX.Element {
  const risk = useRisk();
  const account = useOverview();
  const overview = account.status === "ready" ? account.overview : null;
  const system = risk.status === "ready" ? risk.system : null;
  const unavailable = risk.status === "loading" ? "загружается" : "источник недоступен";

  const portfolio = overview?.portfolio_risk ?? null;
  const entries = system === null
    ? { value: "—", note: unavailable }
    : system.exposure.readiness === "READY"
      ? { value: "Разрешены", note: "блокировок нет" }
      : system.exposure.readiness === "ACCOUNT_BLOCKED"
        ? { value: "Счёт заблокирован", note: `с ${moment(system.exposure.account_blocked_since)}` }
        : system.exposure.readiness === "SLOT_BLOCKED"
          ? { value: "Частично", note: `заблокировано слотов: ${String(system.exposure.blockers.length)}` }
          // Anything else is a state this screen cannot vouch for: it is said
          // as unknown, with its code, never folded into "allowed".
          : { value: "Неизвестно", note: `состояние ${system.exposure.readiness}` };
  const reconciliation = system === null
    ? { value: "—", note: unavailable }
    : system.reconciliation.incomplete === 0
      ? { value: "Полная", note: `по ${moment(system.reconciliation.reconciled_through)} · циклов ${String(system.reconciliation.cycles_tracked)}` }
      : { value: "Неполная", note: `циклов с проблемой сверки: ${String(system.reconciliation.incomplete)}` };
  const positions = overview?.slots.filter((slot) => slot.symbol !== null) ?? [];

  return (
    <div className="flex flex-col gap-2" data-testid="risk-page">
      <PageHeader title="Риски" description="Насколько счёт защищён сейчас: риск портфеля, сверка с биржей и обязательства." />
      {account.status === "not-observed" ? <NotObserved /> : (
        <>
          <LeadStrip
            testId="risk-lead"
            cells={[
              {
                label: "Риск портфеля",
                testId: "risk-portfolio",
                // A stale reading is not today's: the last known state goes
                // to the note, and the figure says it does not know.
                value: portfolio === null || overview?.stale === true
                  ? "Неизвестно"
                  : <span className={PORTFOLIO_TONE[portfolio] ?? "text-text-primary"}>{PORTFOLIO[portfolio] ?? portfolio}</span>,
                note: overview === null
                  ? "счёт не прочитан"
                  : overview.stale
                    ? `данные устарели · последний известный: ${portfolio === null ? "—" : PORTFOLIO[portfolio] ?? portfolio}`
                    : "по текущему снимку счёта",
              },
              { label: "Новые входы", testId: "risk-entries", value: entries.value, note: entries.note },
              { label: "Сверка с биржей", testId: "risk-reconciliation", value: reconciliation.value, note: reconciliation.note },
            ]}
          />
          <div className="grid grid-cols-1 gap-2 lg:grid-cols-12">
            <div className="min-w-0 lg:col-span-5">
              <Panel title="Обязательства, USDT" testId="risk-commitments">
                <dl className="divide-y divide-border-subtle text-body-main">
                  {([
                    ["Маржа в позициях", overview?.position_margin],
                    ["Резерв на докупки по сетке", overview?.reserved_grid_capital],
                    // Margin and the reserve together: what the open cycles
                    // have undertaken, as the server counts it.
                    ["Всего задействовано", overview?.active_capital],
                  ] as const).map(([label, value]) => (
                    <div key={label} className="flex items-baseline justify-between gap-3 py-2 first:pt-0">
                      <dt className="text-text-secondary">{label}</dt>
                      <dd className="text-right tabular-nums text-text-primary">{amount(value)}</dd>
                    </div>
                  ))}
                </dl>
                {overview?.stale === true && <p role="status" className="mt-1 text-helper-sm text-status-warning">Суммы — последний известный снимок.</p>}
              </Panel>
            </div>
            <div className="min-w-0 lg:col-span-7">
              <Panel title="Замечания системы" testId="risk-findings">
                {system === null ? <p className="text-body-main text-text-secondary">Замечания: {unavailable}.</p> : (
                  system.exposure.blockers.length === 0 && system.findings.length === 0 ? (
                    <p className="text-body-main text-text-secondary">Замечаний нет.</p>
                  ) : (
                    <ul className="flex flex-col gap-1 text-body-main text-text-secondary">
                      {system.exposure.blockers.map((b) => (
                        <li key={`${b.slot_id}:${b.reason ?? "?"}`}>
                          {b.symbol} — вход заблокирован{b.since === null ? "" : ` с ${moment(b.since)}`} · <span title={b.reason ?? undefined}>{b.reason === null ? "причина не указана" : reasonText(b.reason)}</span>
                        </li>
                      ))}
                      {system.findings.map((finding) => <li key={finding}>{findingText(finding)}</li>)}
                    </ul>
                  )
                )}
              </Panel>
            </div>
          </div>
          <Panel title="Позиции" testId="risk-positions">
            {positions.length === 0 ? <p className="text-body-main text-text-secondary">Открытых позиций нет.</p> : (
              <div className={TABLE_BLEED}>
                <table className="w-full text-left text-body-main max-sm:block">
                  <thead className={`${TABLE_HEAD} max-sm:hidden`}>
                    <tr>
                      <th className={`${CELL} font-normal`}>Монета</th>
                      <th className={`${CELL} text-right font-normal`}>Позиция · задействовано</th>
                      <th className={`${CELL} font-normal`}>Фаза</th>
                    </tr>
                  </thead>
                  <tbody className="max-sm:block">
                    {positions.map((s) => (
                      <tr key={s.slot_id} className={`${TABLE_ROW} max-sm:grid max-sm:grid-cols-[1fr_auto] max-sm:items-center`}>
                        <td className={`${CELL} break-all font-medium text-text-primary max-sm:col-span-2 max-sm:pb-0`}>{s.symbol}</td>
                        <td className={`${CELL} text-right tabular-nums`}>
                          <span className="flex flex-col leading-tight">
                            <span>{grouped(s.position_size)}</span>
                            {s.undertaken != null && <span className="text-helper-sm text-text-muted">{amount(s.undertaken)} USDT</span>}
                          </span>
                        </td>
                        <td className={CELL}><PhaseBadge phase={s.cycle_phase ?? s.state} /></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Panel>
        </>
      )}
    </div>
  );
}
